import React, { useEffect, useState } from 'react';
import {
  ComboBoxChangeEvent,
  ComboBoxFilterChangeEvent,
  ListItemProps
} from '@progress/kendo-react-dropdowns';
import { Guid } from '@Shared.Angular/@types/guid';
import IFlowModelForMaintenance from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/flowModelForMaintenance';
import { getService } from '@Client/runner.hooks/useService';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';
import { KendoReactComboBox } from '@Shared.Angular/kendo.react/KendoReactComboBox';
import { IRootScopeService } from 'angular';

const MaintenanceFlowModels: React.FC<{ businessId: Guid | null }> = ({
  businessId
}) => {
  const [flowModel, setFlowModel] = useState<IFlowModelForMaintenance | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [flowModels, setFlowModels] = useState<IFlowModelForMaintenance[]>([]);
  const [filterText, setFilterText] = useState<string>('');

  const maintenanceService = getService('maintenanceService');
  const notificationService = getService('notificationService');
  const permissionsService = getService('permissionsService');
  const dialogService = getService('dialogService');
  const flowinglyMomentService = getService('flowinglyMomentService');
  const $rootScope = getService('$rootScope') as IRootScopeService;

  const hasActionsPermission =
    permissionsService.currentUserHasAnyOfPermissions([
      FlowinglyPermissions.ADMIN_ACTIONS
    ]);

  const loadFlowModel = async (value: string) => {
    const flowModelId = value.trim();

    setFlowModel(null);

    if (!businessId || !flowModelId) {
      return;
    }

    setLoading(true);
    try {
      const model = await maintenanceService.getFlowModelForMaintenance(
        businessId,
        flowModelId as unknown as Guid
      );
      setFlowModel(model || null);
    } catch (err) {
      console.error('Error loading flow model:', err);
      notificationService.showErrorToast('Failed to load flow model.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!businessId) {
      return;
    }
    setFilterText('');
    setFlowModel(null);
  }, [businessId]);

  useEffect(() => {
    if (!businessId) {
      setFlowModels([]);
      return;
    }

    let cancelled = false;
    const newTimeoutId = setTimeout(async () => {
      try {
        const models = await maintenanceService.getFlowModelsForMaintenance(
          businessId,
          filterText.trim()
        );
        if (!cancelled) {
          setFlowModels(models || []);
        }
      } catch (err) {
        console.error('Error loading flow model list:', err);
        if (!cancelled) {
          notificationService.showErrorToast('Failed to load flow model list.');
        }
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(newTimeoutId);
    };
  }, [businessId, filterText]);

  const selectFlowModel = (item: IFlowModelForMaintenance) => {
    setFilterText(item.name);
    loadFlowModel(String(item.id));
  };

  const handleFilterChange = (event: ComboBoxFilterChangeEvent) => {
    setFilterText(event.filter?.value ?? '');
  };

  const handleSelectionChange = (event: ComboBoxChangeEvent) => {
    const item = event.value as IFlowModelForMaintenance | null;
    if (item) {
      selectFlowModel(item);
    } else {
      setFilterText('');
      setFlowModel(null);
    }
  };

  const itemRender = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    const item = itemProps.dataItem as IFlowModelForMaintenance;
    if (item.active) {
      return li;
    }
    return React.cloneElement(
      li,
      { ...li.props, className: (li.props.className ?? '') + ' deleted' },
      <span className="flow-model-dropdown-item-content">
        <span>{item.name}</span>
        <span className="flow-model-dropdown-item-deleted">
          {`(Deleted ${flowinglyMomentService.formatFullDate(
            item.updatedDateUtc,
            'YYYY-MM-DD HH:mm'
          )})`}
        </span>
      </span>
    );
  };

  const undeleteFlowModel = () => {
    if (!businessId || !flowModel) {
      return;
    }
    dialogService
      .showConfirmDialog($rootScope.$new(), {
        title: `Restore Flow Model`,
        message: `Are you sure you want to restore this flow model "${flowModel.name}"?`
      })
      .then(async () => {
        setLoading(true);
        try {
          await maintenanceService.restoreFlowModel(businessId, flowModel.id);
          const updated = await maintenanceService.getFlowModelForMaintenance(
            businessId,
            flowModel.id
          );
          setFlowModel(updated || null);
          setFlowModels((models) =>
            models.map((model) =>
              model.id === flowModel.id ? { ...model, active: true } : model
            )
          );
          notificationService.showSuccessToast('Flow model restored.');
        } catch (err) {
          console.error('Error restoring flow model:', err);
          notificationService.showErrorToast('Failed to restore flow model.');
        } finally {
          setLoading(false);
        }
      });
  };

  if (!businessId) {
    return (
      <div className="maintenance-flow-models">
        <p>Please select a business to view flow models.</p>
      </div>
    );
  }

  return (
    <div className="maintenance-flow-models">
      <div className="flow-model-controls">
        <div className="flow-model-control flow-model-search">
          <label>Flow Model</label>
          <KendoReactComboBox
            className="flow-model-combobox"
            popupClassName="flow-model-combobox-popup"
            data={flowModels}
            textField="name"
            dataItemKey="id"
            placeholder="Search flow models by name or flow model Id"
            value={flowModel}
            filterable={true}
            clearButton={true}
            loading={loading}
            onFilterChange={handleFilterChange}
            onChange={handleSelectionChange}
            itemRender={itemRender}
          />
        </div>
        <div className="flow-model-control">
          {flowModel && !flowModel.active && hasActionsPermission && (
            <TextButton
              type="danger"
              text="Un-delete"
              title="Restore this flow model to the library"
              disabled={loading}
              onClick={undeleteFlowModel}
            />
          )}
        </div>
      </div>

      {!loading && flowModel && (
        <div className="flow-model-details">
          <div className="flow-model-fields">
            <div>
              <label>Name:</label>
              <span>{flowModel.name}</span>
            </div>
            <div>
              <label>Active:</label>
              <span>{flowModel.active ? 'Yes' : 'No'}</span>
            </div>
            <div>
              <label>Type:</label>
              <span>{flowModel.type}</span>
            </div>
            {!flowModel.active && (
              <div>
                <label>Deleted By:</label>
                <span>{flowModel.deletedByUserEmail}</span>
              </div>
            )}
          </div>

          {flowModel.flowModelSchemas
            .toSorted((a, b) => a.createdUtc.localeCompare(b.createdUtc))
            .reverse()
            .map((schema) => (
              <div
                key={schema.id}
                className={
                  'flow-model-schema' + (!schema.isObsolete ? ' active' : '')
                }
              >
                <div>
                  <label>Schema Id:</label>
                  <span>{schema.id}</span>
                </div>
                <div>
                  <label>Schema Version:</label>
                  <span>{schema.version}</span>
                </div>
                <div>
                  <label>Is Obsolete:</label>
                  <span>{schema.isObsolete ? 'Yes' : 'No'}</span>
                </div>
                <div>
                  <label>Publish Type:</label>
                  <span>{schema.publishType}</span>
                </div>
                <div>
                  <label>Created:</label>
                  <span>
                    {flowinglyMomentService.formatFullDate(
                      schema.createdUtc,
                      'YYYY-MM-DD HH:mm:ss'
                    )}{' '}
                    ({schema.createdUtc.replace('T', ' ')} UTC)
                  </span>
                </div>
                <div>
                  <label>Last Updated:</label>
                  <span>
                    {flowinglyMomentService.formatFullDate(
                      schema.lastUpdatedUtc,
                      'YYYY-MM-DD HH:mm:ss'
                    )}{' '}
                    ({schema.lastUpdatedUtc.replace('T', ' ')} UTC)
                  </span>
                </div>
                {schema.publishRequestedByUserEmail && (
                  <div>
                    <label>Publish Requested By:</label>
                    <span>{schema.publishRequestedByUserEmail}</span>
                  </div>
                )}
                {schema.publishedByUserEmail && (
                  <div>
                    <label>Published By:</label>
                    <span>{schema.publishedByUserEmail}</span>
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default MaintenanceFlowModels;
